import type {MotionValue} from 'framer-motion'
import {motion, useSpring, useTransform} from 'framer-motion'
import type {CSSProperties} from 'react'
import {useEffect} from 'react'

interface NumberProps {
  mv: MotionValue<number>
  number: number
  height: number
}

function Number({mv, number, height}: NumberProps) {
  const y = useTransform(mv, (latest: number) => {
    const placeValue = latest % 10
    const offset = (10 + number - placeValue) % 10
    let memo = offset * height

    if (offset > 5) {
      memo -= 10 * height
    }

    return memo
  })

  const style: CSSProperties = {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return <motion.span style={{...style, y}}>{number}</motion.span>
}

interface DigitProps {
  place: number
  value: number
  height: number
  digitStyle?: CSSProperties
}

function Digit({place, value, height, digitStyle}: DigitProps) {
  const valueRoundedToPlace = Math.floor(value / place)
  const animatedValue: MotionValue<number> = useSpring(valueRoundedToPlace)

  // Animate the digit when the value changes
  useEffect(() => {
    animatedValue.set(valueRoundedToPlace)
  }, [animatedValue, valueRoundedToPlace])

  const defaultStyle: CSSProperties = {
    height,
    position: 'relative' as const,
    width: '1ch',
    fontVariantNumeric: 'tabular-nums' as const,
  }

  return (
    <div style={{...defaultStyle, ...digitStyle}}>
      {Array.from({length: 10}, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} height={height} />
      ))}
    </div>
  )
}

interface CounterProps {
  value: number
  fontSize?: number
  padding?: number
  places?: number[]
  gap?: number
  borderRadius?: number
  horizontalPadding?: number
  textColor?: string
  fontWeight?: string | number
  containerStyle?: CSSProperties
  counterStyle?: CSSProperties
  digitStyle?: CSSProperties
  gradientHeight?: number
  gradientFrom?: string
  gradientTo?: string
  topGradientStyle?: CSSProperties
  bottomGradientStyle?: CSSProperties
  textBefore?: string
  textAfter?: string
}

export default function Counter({
  value,
  fontSize = 100,
  padding = 0,
  places = [100, 10, 1],
  gap = 2,
  borderRadius = 4,
  horizontalPadding = 8,
  textColor = 'black',
  fontWeight = 'bold',
  containerStyle,
  counterStyle,
  digitStyle,
  gradientHeight = 16,
  topGradientStyle,
  bottomGradientStyle,
  textBefore,
  textAfter,
}: CounterProps) {
  const height = fontSize + padding

  const defaultContainerStyle: CSSProperties = {
    position: 'relative' as const,
    display: 'inline-block' as const,
  }

  const defaultCounterStyle: CSSProperties = {
    fontSize,
    display: 'flex' as const,
    gap: gap,
    overflow: 'hidden' as const,
    borderRadius: borderRadius,
    paddingLeft: horizontalPadding,
    paddingRight: horizontalPadding,
    lineHeight: 1,
    color: textColor,
    fontWeight: fontWeight,
    alignItems: 'center',
  }

  const gradientContainerStyle: CSSProperties = {
    pointerEvents: 'none' as const,
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  }

  const defaultTopGradientStyle: CSSProperties = {
    height: gradientHeight,
  }

  const defaultBottomGradientStyle: CSSProperties = {
    position: 'absolute' as const,
    bottom: 0,
    width: '100%',
    height: gradientHeight,
  }

  return (
    <div style={{...defaultContainerStyle, ...containerStyle}}>
      <div style={{...defaultCounterStyle, ...counterStyle}}>
        {textBefore && <span>{textBefore}</span>}
        {places.map((place) => (
          <Digit key={place} place={place} value={value} height={height} digitStyle={digitStyle} />
        ))}
        {textAfter && <span>{textAfter}</span>}
      </div>
      <div style={gradientContainerStyle}>
        <div style={topGradientStyle ? topGradientStyle : defaultTopGradientStyle} />
        <div style={bottomGradientStyle ? bottomGradientStyle : defaultBottomGradientStyle} />
      </div>
    </div>
  )
}
